import { createElement, clearNode, setHidden, focusNode } from "../shared/dom.js"
import { buildShell, button, announce } from "../shared/chrome.js"
import { maybeShuffle } from "../shared/random.js"
import { buildResult, extraKeys } from "../shared/result.js"
import { langValue, isPlainObject, nonEmptyString } from "../validate.js"

export const TYPE = "mcq_quiz"
export const OUTCOMES = ["correct", "incorrect"]
export const CORRECT_OUTCOMES = ["correct"]

const ITEM_KEYS = ["id", "stem", "options", "explanation", "distractors"]

function normaliseOptions(item) {
  const options = Array.isArray(item?.options) ? item.options : []

  return options
    .filter((option) => isPlainObject(option) && nonEmptyString(option.text))
    .map((option, index) => ({
      id: nonEmptyString(option.id) ? option.id : `o${index + 1}`,
      lang: typeof option.lang === "string" ? option.lang : null,
      text: option.text,
      correct: option.correct === true
    }))
}

export function usableItems(exercise) {
  const items = Array.isArray(exercise?.items) ? exercise.items : []

  return items
    .map((item) => ({ item, options: normaliseOptions(item) }))
    .filter(({ item, options }) => {
      if (!langValue(item?.stem)) return false
      if (options.length < 2) return false
      return options.filter((option) => option.correct).length === 1
    })
}

export function validate(exercise) {
  if (usableItems(exercise).length === 0) {
    return {
      code: "no_usable_items",
      message: "No quiz items have a stem and exactly one correct option"
    }
  }
  return null
}

function groupName(exercise, index) {
  const base = typeof exercise?.id === "string" ? exercise.id.replace(/\W+/g, "-") : "exercise"
  return `exw-${base}-${index}`
}

export function mount(exercise, node, options) {
  const { t, now, random, callbacks } = options
  const config = exercise.config && typeof exercise.config === "object" ? exercise.config : {}
  const explanationMode = config.reveal_explanation === "never" ? "never" : "after_answer"

  const shell = buildShell(node, { exercise, t, modifier: "mcq-quiz" })
  const attempts = []

  const queue = maybeShuffle(usableItems(exercise), config.shuffle_items === true, random)
    .map(({ item, options: opts }) => ({
      item,
      options: maybeShuffle(opts, config.shuffle_options === true, random)
    }))

  const stem = createElement("legend", { class: "exw__stem" })
  const optionList = createElement("div", { class: "exw__options" })
  const fieldset = createElement("fieldset", { class: "exw__card" }, [stem, optionList])
  const feedback = createElement("div", {
    class: "exw__feedback",
    "aria-live": "polite",
    "aria-atomic": "true"
  })
  shell.body.appendChild(fieldset)
  shell.body.appendChild(feedback)

  const checkBtn = button("exw__btn--primary", t("check"), "check")
  const nextBtn = button("exw__btn--primary", t("next"), "next")
  shell.actions.appendChild(checkBtn)
  shell.actions.appendChild(nextBtn)
  setHidden(nextBtn, true)

  let position = 0
  let answered = false
  let startedAt = now()
  let finished = false
  let inputs = []

  function current() {
    return queue[position]
  }

  function isLast() {
    return position === queue.length - 1
  }

  function renderQuestion() {
    const entry = current()
    const item = entry.item

    stem.textContent = item.stem.text
    if (typeof item.stem.lang === "string" && item.stem.lang) {
      stem.setAttribute("lang", item.stem.lang)
    } else {
      stem.removeAttribute("lang")
    }

    clearNode(optionList)
    clearNode(feedback)
    inputs = []

    const name = groupName(exercise, position)
    entry.options.forEach((option) => {
      const input = createElement("input", {
        type: "radio",
        class: "exw__option-input",
        name,
        value: option.id
      })
      const label = createElement("label", { class: "exw__option" }, [
        input,
        createElement("span", { class: "exw__option-mark", "aria-hidden": "true" }),
        createElement("span", {
          class: "exw__option-text",
          lang: option.lang || null,
          text: option.text
        })
      ])
      optionList.appendChild(label)
      inputs.push({ input, label, option })
    })

    answered = false
    startedAt = now()
    shell.progress.textContent = t("progress", { current: position + 1, total: queue.length })
    setHidden(checkBtn, false)
    setHidden(nextBtn, true)
    nextBtn.textContent = isLast() ? t("finish") : t("next")
  }

  function selected() {
    return inputs.find(({ input }) => input.checked) || null
  }

  function renderFeedback(entry, picked) {
    const correctOption = entry.options.find((option) => option.correct)
    const isCorrect = picked.option.correct

    inputs.forEach(({ input, label, option }) => {
      input.disabled = true
      if (option.correct) {
        label.dataset.exwState = "correct"
        label.querySelector(".exw__option-mark").textContent = "✓"
      } else if (option.id === picked.option.id) {
        label.dataset.exwState = "incorrect"
        label.querySelector(".exw__option-mark").textContent = "✕"
      }
    })

    clearNode(feedback)
    feedback.dataset.exwState = isCorrect ? "correct" : "incorrect"

    const headline = createElement("p", { class: "exw__feedback-headline" }, [
      createElement("span", {
        class: "exw__feedback-icon",
        "aria-hidden": "true",
        text: isCorrect ? "✓" : "✕"
      }),
      createElement("span", {
        class: "exw__feedback-text",
        text: isCorrect ? t("correct") : t("incorrect")
      })
    ])
    feedback.appendChild(headline)

    if (!isCorrect) {
      feedback.appendChild(
        createElement("p", {
          class: "exw__feedback-answer",
          text: t("correct_answer_is", { answer: correctOption.text })
        })
      )
    }

    if (explanationMode === "after_answer" && langValue(entry.item.explanation)) {
      feedback.appendChild(
        createElement("p", { class: "exw__explanation" }, [
          createElement("span", { class: "exw__detail-label", text: t("explanation_label") }),
          createElement("span", {
            class: "exw__detail-value",
            lang: entry.item.explanation.lang || null,
            text: entry.item.explanation.text
          })
        ])
      )
    }
  }

  function check() {
    if (finished || answered) return

    const picked = selected()
    if (!picked) {
      announce(shell.status, t("choose_an_option"))
      return
    }

    answered = true
    const entry = current()
    const ms = Math.max(0, now() - startedAt)
    const outcome = picked.option.correct ? "correct" : "incorrect"

    attempts.push({
      id: entry.item.id,
      outcome,
      ms,
      picked: picked.option.id,
      extra: extraKeys(entry.item, ITEM_KEYS)
    })

    if (typeof callbacks.onItem === "function") {
      callbacks.onItem({ id: entry.item.id, outcome, ms, picked: picked.option.id })
    }

    renderFeedback(entry, picked)
    setHidden(checkBtn, true)
    setHidden(nextBtn, false)
    focusNode(nextBtn)
  }

  function finish() {
    finished = true
    clearNode(shell.actions)
    clearNode(shell.body)
    shell.progress.textContent = ""

    const result = buildResult(exercise, attempts, { now, correctOutcomes: CORRECT_OUTCOMES })
    const heading = createElement("h4", {
      class: "exw__summary-heading",
      tabindex: "-1",
      text: t("finished_quiz", { correct: result.score.correct, total: result.score.total })
    })
    shell.body.appendChild(createElement("div", { class: "exw__summary" }, [heading]))
    focusNode(heading)
    announce(shell.status, heading.textContent)

    if (typeof callbacks.onComplete === "function") callbacks.onComplete(result)
  }

  function next() {
    if (finished || !answered) return
    if (isLast()) {
      finish()
      return
    }

    position += 1
    renderQuestion()
    announce(shell.status, t("question_announced", { current: position + 1, total: queue.length }))
    focusNode(inputs[0] ? inputs[0].input : checkBtn)
  }

  checkBtn.addEventListener("click", check)
  nextBtn.addEventListener("click", next)

  renderQuestion()

  return {
    unmount() {
      checkBtn.removeEventListener("click", check)
      nextBtn.removeEventListener("click", next)
      clearNode(node)
    }
  }
}

export default { type: TYPE, validate, mount }
